import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

interface Problem {
  id: number;
  beforeTitle: string;
  afterTitle: string;
  beforeDescription: string;
  afterDescription: string;
  beforeIcon: string;
  afterIcon: string;
}

const problems: Problem[] = [
  {
    id: 1,
    beforeTitle: 'Physical Attendance Book',
    afterTitle: 'Digital Attendance Dashboard',
    beforeDescription: 'Manual sign-in sheets, lost records, no visibility',
    afterDescription: 'Real-time tracking, GPS check-in, instant reports',
    beforeIcon: 'ri-book-2-line',
    afterIcon: 'ri-calendar-check-line',
  },
  {
    id: 2,
    beforeTitle: 'Inventory in Spreadsheets',
    afterTitle: 'Real-Time Inventory Tracker',
    beforeDescription: 'Outdated stock counts, stockouts, manual errors',
    afterDescription: 'Live stock monitoring, auto-alerts, barcode scanning',
    beforeIcon: 'ri-file-excel-2-line',
    afterIcon: 'ri-stack-line',
  },
  {
    id: 3,
    beforeTitle: 'Orders Tracked in Chats',
    afterTitle: 'Automated Order Manager',
    beforeDescription: 'Lost in messages, missed orders, no status tracking',
    afterDescription: 'Centralized order system, status updates, auto-notify',
    beforeIcon: 'ri-whatsapp-line',
    afterIcon: 'ri-shopping-bag-3-line',
  },
  {
    id: 4,
    beforeTitle: 'Invoices Created by Hand',
    afterTitle: 'Automated Billing System',
    beforeDescription: 'Manual calculations, errors, slow payment collection',
    afterDescription: 'Auto-generated invoices, payment reminders, tracking',
    beforeIcon: 'ri-pencil-line',
    afterIcon: 'ri-file-list-3-line',
  },
  {
    id: 5,
    beforeTitle: 'Manual Schedules',
    afterTitle: 'Automated Scheduling System',
    beforeDescription: 'Conflicting appointments, no-shows, confusion',
    afterDescription: 'Smart calendar sync, auto-reminders, conflict detection',
    beforeIcon: 'ri-calendar-2-line',
    afterIcon: 'ri-calendar-check-2-line',
  },
  {
    id: 6,
    beforeTitle: 'No Clear Business Insights',
    afterTitle: 'AI Performance Dashboard',
    beforeDescription: 'Guessing decisions, no data, reactive management',
    afterDescription: 'Data-driven insights, trend analysis, smart alerts',
    beforeIcon: 'ri-question-mark',
    afterIcon: 'ri-bar-chart-grouped-line',
  },
];

// Hand-drawn SVG wireframes for "before" state
function HandDrawnBefore({ id }: { id: number }) {
  return (
    <svg
      viewBox="0 0 260 200"
      className="w-full h-full"
      style={{ fontFamily: "'Caveat', cursive" }}
    >
      {/* Rough paper texture lines */}
      <line x1="0" y1="198" x2="260" y2="198" stroke="#d4c5a0" strokeWidth="1" />

      {id === 1 && (
        // Attendance book - messy notebook
        <g>
          {/* Notebook outline - wobbly */}
          <path d="M 20 15 Q 21 14 240 16 Q 242 15 241 185 Q 240 187 19 186 Q 18 185 20 15" fill="#fef9ec" stroke="#8b7355" strokeWidth="2.5" strokeLinejoin="round" />
          {/* Spiral binding */}
          {[30, 50, 70, 90, 110, 130, 150, 170].map((y, i) => (
            <ellipse key={i} cx="28" cy={y} rx="6" ry="5" fill="none" stroke="#8b7355" strokeWidth="1.5" />
          ))}
          {/* Title line */}
          <path d="M 45 35 Q 180 33 215 36" stroke="#8b7355" strokeWidth="2" fill="none" />
          <text x="50" y="32" fontSize="11" fill="#5c4a2a" fontWeight="bold">Attendance Register</text>
          {/* Ruled lines */}
          {[55, 75, 95, 115, 135, 155, 175].map((y, i) => (
            <path key={i} d={`M 45 ${y} Q ${130 + (i % 3) * 5} ${y - 1} 220 ${y}`} stroke="#c8b89a" strokeWidth="1" fill="none" />
          ))}
          {/* Handwritten entries */}
          <text x="50" y="70" fontSize="9" fill="#3d2b1f">Raj Kumar - ✓</text>
          <text x="50" y="90" fontSize="9" fill="#3d2b1f">Priya S. - absent??</text>
          <text x="50" y="110" fontSize="9" fill="#3d2b1f" style={{ textDecoration: 'line-through' }}>Amit - late</text>
          <text x="50" y="130" fontSize="9" fill="#e53e3e">??? - forgot to sign</text>
          <text x="50" y="150" fontSize="9" fill="#3d2b1f">Sunita - ✓</text>
          <text x="50" y="170" fontSize="9" fill="#3d2b1f">Ravi - ½ day?</text>
          {/* Scribble cross-out */}
          <path d="M 48 103 Q 120 108 180 105" stroke="#e53e3e" strokeWidth="1.5" fill="none" strokeDasharray="3,2" />
          {/* Coffee stain */}
          <ellipse cx="195" cy="140" rx="18" ry="14" fill="none" stroke="#c8a96e" strokeWidth="1" opacity="0.5" />
          <ellipse cx="195" cy="140" rx="12" ry="9" fill="none" stroke="#c8a96e" strokeWidth="0.8" opacity="0.4" />
        </g>
      )}

      {id === 2 && (
        // Spreadsheet chaos
        <g>
          {/* Excel window */}
          <rect x="15" y="10" width="230" height="180" rx="3" fill="#f0f4f0" stroke="#5a7a5a" strokeWidth="2" />
          <rect x="15" y="10" width="230" height="22" rx="3" fill="#217346" />
          <text x="25" y="25" fontSize="9" fill="white">Inventory_Final_v3_ACTUAL_USE_THIS.xlsx</text>
          {/* Column headers - wobbly */}
          <rect x="15" y="32" width="230" height="16" fill="#e8f0e8" stroke="#5a7a5a" strokeWidth="0.5" />
          <text x="20" y="44" fontSize="8" fill="#217346" fontWeight="bold">Item</text>
          <text x="90" y="44" fontSize="8" fill="#217346" fontWeight="bold">Stock</text>
          <text x="140" y="44" fontSize="8" fill="#217346" fontWeight="bold">Last Updated</text>
          <text x="200" y="44" fontSize="8" fill="#217346" fontWeight="bold">Status</text>
          {/* Rows */}
          {[
            { item: 'Widget A', stock: '???', date: '3 wks ago', status: 'OUT?', statusColor: '#e53e3e' },
            { item: 'Widget B', stock: '12 (old)', date: 'unknown', status: 'ok?', statusColor: '#d69e2e' },
            { item: 'Widget C', stock: '', date: '', status: '???', statusColor: '#e53e3e' },
            { item: 'Widget D', stock: '45', date: 'yesterday', status: '✓', statusColor: '#38a169' },
            { item: 'Widget E', stock: 'check room', date: '???', status: '!', statusColor: '#e53e3e' },
          ].map((row, i) => (
            <g key={i}>
              <rect x="15" y={48 + i * 22} width="230" height="22" fill={i % 2 === 0 ? 'white' : '#f7faf7'} stroke="#c8d8c8" strokeWidth="0.5" />
              <text x="20" y={63 + i * 22} fontSize="8" fill="#2d3748">{row.item}</text>
              <text x="90" y={63 + i * 22} fontSize="8" fill={row.statusColor}>{row.stock}</text>
              <text x="140" y={63 + i * 22} fontSize="7" fill="#718096">{row.date}</text>
              <text x="205" y={63 + i * 22} fontSize="8" fill={row.statusColor} fontWeight="bold">{row.status}</text>
            </g>
          ))}
          {/* Scribbled note */}
          <rect x="130" y="148" width="100" height="35" fill="#fffde7" stroke="#f6c90e" strokeWidth="1.5" transform="rotate(-3 180 165)" />
          <text x="135" y="162" fontSize="7" fill="#5c4a2a" transform="rotate(-3 180 165)">Ask Suresh which</text>
          <text x="135" y="172" fontSize="7" fill="#5c4a2a" transform="rotate(-3 180 165)">version is correct!</text>
        </g>
      )}

      {id === 3 && (
        // WhatsApp chat chaos
        <g>
          {/* Phone outline */}
          <rect x="70" y="5" width="120" height="190" rx="12" fill="#e8f5e9" stroke="#4a7c59" strokeWidth="2" />
          <rect x="70" y="5" width="120" height="30" rx="12" fill="#075e54" />
          <rect x="70" y="20" width="120" height="15" fill="#075e54" />
          <text x="130" y="24" fontSize="9" fill="white" textAnchor="middle">Orders Group 📦</text>
          <text x="130" y="33" fontSize="7" fill="#a8d5a2" textAnchor="middle">47 unread messages</text>
          {/* Chat bubbles */}
          <rect x="80" y="45" width="80" height="22" rx="8" fill="#dcf8c6" />
          <text x="88" y="57" fontSize="7" fill="#2d3748">Order #45 - 3 boxes</text>
          <text x="88" y="65" fontSize="6" fill="#718096">Ravi • 9:14 AM</text>

          <rect x="100" y="75" width="75" height="22" rx="8" fill="white" />
          <text x="108" y="87" fontSize="7" fill="#2d3748">Which warehouse?</text>
          <text x="108" y="95" fontSize="6" fill="#718096">You • 9:32 AM</text>

          <rect x="80" y="105" width="85" height="22" rx="8" fill="#dcf8c6" />
          <text x="88" y="117" fontSize="7" fill="#2d3748">Paid 5000 for order</text>
          <text x="88" y="125" fontSize="6" fill="#718096">Priya • 11:05 AM</text>

          <rect x="80" y="135" width="90" height="22" rx="8" fill="#dcf8c6" />
          <text x="88" y="147" fontSize="7" fill="#e53e3e">Which order?? 😤</text>
          <text x="88" y="155" fontSize="6" fill="#718096">Ravi • 11:47 AM</text>

          <rect x="80" y="165" width="95" height="22" rx="8" fill="#dcf8c6" />
          <text x="88" y="177" fontSize="7" fill="#2d3748">Scroll up... 500 msgs</text>
          <text x="88" y="185" fontSize="6" fill="#718096">You • 2:10 PM</text>
        </g>
      )}

      {id === 4 && (
        // Hand-written invoice
        <g>
          {/* Paper */}
          <rect x="20" y="8" width="220" height="184" fill="#fffef5" stroke="#c8b89a" strokeWidth="2" />
          {/* Torn edge effect */}
          <path d="M 20 8 Q 25 6 30 9 Q 35 7 40 9 Q 45 7 50 9 Q 55 7 60 9 Q 65 7 70 9 Q 75 7 80 9 Q 85 7 90 9 Q 95 7 100 9 Q 105 7 110 9 Q 115 7 120 9 Q 125 7 130 9 Q 135 7 140 9 Q 145 7 150 9 Q 155 7 160 9 Q 165 7 170 9 Q 175 7 180 9 Q 185 7 190 9 Q 195 7 200 9 Q 205 7 210 9 Q 215 7 220 9 Q 225 7 240 8" fill="none" stroke="#c8b89a" strokeWidth="1" />
          {/* Header */}
          <text x="130" y="28" fontSize="13" fill="#2d3748" fontWeight="bold" textAnchor="middle">INVOICE</text>
          <path d="M 40 33 Q 130 31 220 33" stroke="#c8b89a" strokeWidth="1.5" fill="none" />
          {/* Fields */}
          <text x="30" y="50" fontSize="8" fill="#718096">To: Sharma Traders</text>
          <text x="30" y="63" fontSize="8" fill="#718096">Date: </text>
          <text x="55" y="63" fontSize="8" fill="#e53e3e">what date??</text>
          <path d="M 30 70 Q 220 68 220 70" stroke="#e8e0d0" strokeWidth="1" fill="none" />
          {/* Items table */}
          <text x="30" y="83" fontSize="8" fill="#2d3748" fontWeight="bold">Item</text>
          <text x="140" y="83" fontSize="8" fill="#2d3748" fontWeight="bold">Qty</text>
          <text x="185" y="83" fontSize="8" fill="#2d3748" fontWeight="bold">Total</text>
          <path d="M 30 87 Q 220 85 220 87" stroke="#c8b89a" strokeWidth="1" fill="none" />
          {[
            { item: 'Product A', qty: '5', total: '₹2500' },
            { item: 'Product B', qty: '???', total: '???' },
            { item: 'Delivery', qty: '1', total: '₹200' },
          ].map((row, i) => (
            <g key={i}>
              <text x="30" y={100 + i * 18} fontSize="8" fill="#2d3748">{row.item}</text>
              <text x="145" y={100 + i * 18} fontSize="8" fill={row.qty === '???' ? '#e53e3e' : '#2d3748'}>{row.qty}</text>
              <text x="185" y={100 + i * 18} fontSize="8" fill={row.total === '???' ? '#e53e3e' : '#2d3748'}>{row.total}</text>
            </g>
          ))}
          <path d="M 30 158 Q 220 156 220 158" stroke="#c8b89a" strokeWidth="1" fill="none" />
          <text x="140" y="172" fontSize="9" fill="#2d3748" fontWeight="bold">Total:</text>
          <text x="185" y="172" fontSize="9" fill="#e53e3e" fontWeight="bold">₹???</text>
          {/* Scribble */}
          <text x="30" y="188" fontSize="7" fill="#e53e3e" style={{ fontStyle: 'italic' }}>recalculate this!!</text>
          {/* Correction marks */}
          <path d="M 185 95 L 215 95" stroke="#e53e3e" strokeWidth="1.5" />
          <text x="185" y="108" fontSize="8" fill="#e53e3e">₹2,700?</text>
        </g>
      )}

      {id === 5 && (
        // Paper calendar chaos
        <g>
          {/* Calendar grid */}
          <rect x="15" y="10" width="230" height="180" rx="4" fill="#fff8f0" stroke="#c8a96e" strokeWidth="2" />
          <rect x="15" y="10" width="230" height="28" rx="4" fill="#c8a96e" />
          <text x="130" y="28" fontSize="11" fill="white" textAnchor="middle" fontWeight="bold">March Schedule</text>
          {/* Day headers */}
          {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((d, i) => (
            <text key={i} x={22 + i * 33} y={52} fontSize="7" fill="#718096" fontWeight="bold">{d}</text>
          ))}
          {/* Calendar cells */}
          {Array.from({ length: 28 }, (_, i) => {
            const col = i % 7;
            const row = Math.floor(i / 7);
            const x = 15 + col * 33;
            const y = 55 + row * 35;
            return (
              <g key={i}>
                <rect x={x} y={y} width="32" height="34" fill="white" stroke="#e8d5b0" strokeWidth="0.5" />
                <text x={x + 4} y={y + 11} fontSize="7" fill="#718096">{i + 1}</text>
              </g>
            );
          })}
          {/* Scribbled appointments */}
          <text x="20" y="80" fontSize="6" fill="#e53e3e" transform="rotate(-5 50 80)">Raj mtg</text>
          <text x="53" y="78" fontSize="6" fill="#2d3748" transform="rotate(3 70 78)">??</text>
          <text x="86" y="82" fontSize="6" fill="#e53e3e">FULL</text>
          <text x="20" y="115" fontSize="6" fill="#2d3748">Priya</text>
          <text x="53" y="113" fontSize="6" fill="#e53e3e" transform="rotate(-4 70 113)">conflict!</text>
          <text x="119" y="117" fontSize="6" fill="#2d3748">call</text>
          <text x="20" y="150" fontSize="6" fill="#2d3748">Amit</text>
          <text x="86" y="148" fontSize="6" fill="#e53e3e">DOUBLE</text>
          <text x="86" y="157" fontSize="6" fill="#e53e3e">BOOKED</text>
          {/* Cross-outs */}
          <path d="M 48 105 L 80 120" stroke="#e53e3e" strokeWidth="1.5" />
          <path d="M 80 105 L 48 120" stroke="#e53e3e" strokeWidth="1.5" />
        </g>
      )}

      {id === 6 && (
        // No insights - question marks and confusion
        <g>
          {/* Whiteboard */}
          <rect x="15" y="10" width="230" height="180" rx="4" fill="#f8f9fa" stroke="#9ca3af" strokeWidth="2" />
          <rect x="15" y="10" width="230" height="22" rx="4" fill="#6b7280" />
          <text x="130" y="25" fontSize="9" fill="white" textAnchor="middle">Business Performance</text>
          {/* Confused charts */}
          {/* Squiggly line chart */}
          <path d="M 30 80 Q 50 60 70 90 Q 90 50 110 100 Q 130 40 150 85 Q 170 110 190 70 Q 210 90 230 60" stroke="#9ca3af" strokeWidth="2" fill="none" strokeDasharray="4,2" />
          <text x="30" y="55" fontSize="8" fill="#9ca3af">Sales?</text>
          {/* Big question marks */}
          <text x="55" y="130" fontSize="40" fill="#e5e7eb" fontWeight="bold">?</text>
          <text x="120" y="145" fontSize="50" fill="#e5e7eb" fontWeight="bold">?</text>
          <text x="185" y="125" fontSize="35" fill="#e5e7eb" fontWeight="bold">?</text>
          {/* Sticky notes */}
          <rect x="25" y="150" width="65" height="35" fill="#fef08a" stroke="#ca8a04" strokeWidth="1" transform="rotate(-4 57 167)" />
          <text x="30" y="165" fontSize="7" fill="#5c4a2a" transform="rotate(-4 57 167)">Are we making</text>
          <text x="30" y="175" fontSize="7" fill="#5c4a2a" transform="rotate(-4 57 167)">money?? 🤷</text>
          <rect x="160" y="148" width="65" height="35" fill="#fef08a" stroke="#ca8a04" strokeWidth="1" transform="rotate(3 192 165)" />
          <text x="165" y="163" fontSize="7" fill="#5c4a2a" transform="rotate(3 192 165)">Which product</text>
          <text x="165" y="173" fontSize="7" fill="#5c4a2a" transform="rotate(3 192 165)">sells most??</text>
        </g>
      )}
    </svg>
  );
}

// Clean digital dashboard for "after" state
function CleanDashboard({ id }: { id: number }) {
  return (
    <svg viewBox="0 0 260 200" className="w-full h-full">
      {id === 1 && (
        <g>
          <rect x="10" y="8" width="240" height="184" rx="10" fill="white" stroke="#14B8A6" strokeWidth="2" />
          <rect x="10" y="8" width="240" height="36" rx="10" fill="#14B8A6" />
          <rect x="10" y="30" width="240" height="14" fill="#14B8A6" />
          <text x="130" y="30" fontSize="10" fill="white" textAnchor="middle" fontWeight="bold">Attendance Dashboard</text>
          <text x="130" y="42" fontSize="7" fill="#a7f3d0" textAnchor="middle">Live • Updated just now</text>
          {/* Stats row */}
          {[
            { label: 'Present', value: '18', color: '#10b981' },
            { label: 'Absent', value: '2', color: '#ef4444' },
            { label: 'Late', value: '1', color: '#f59e0b' },
          ].map((stat, i) => (
            <g key={i}>
              <rect x={18 + i * 80} y="52" width="72" height="40" rx="6" fill={`${stat.color}15`} stroke={stat.color} strokeWidth="1.5" />
              <text x={54 + i * 80} y="72" fontSize="16" fill={stat.color} fontWeight="bold" textAnchor="middle">{stat.value}</text>
              <text x={54 + i * 80} y="84" fontSize="7" fill="#6b7280" textAnchor="middle">{stat.label}</text>
            </g>
          ))}
          {/* Employee list */}
          {[
            { name: 'Raj Kumar', time: '9:02 AM', status: '✓', color: '#10b981' },
            { name: 'Priya Sharma', time: '9:15 AM', status: '✓', color: '#10b981' },
            { name: 'Amit Singh', time: '—', status: '✗', color: '#ef4444' },
            { name: 'Sunita Rao', time: '9:08 AM', status: '✓', color: '#10b981' },
            { name: 'Ravi Patel', time: '10:30 AM', status: '⏰', color: '#f59e0b' },
          ].map((emp, i) => (
            <g key={i}>
              <rect x="14" y={100 + i * 18} width="232" height="16" rx="4" fill={i % 2 === 0 ? '#f0fdfa' : 'white'} />
              <circle cx="26" cy={108 + i * 18} r="5" fill="#14B8A6" />
              <text x="36" y={112 + i * 18} fontSize="8" fill="#1f2937">{emp.name}</text>
              <text x="155" y={112 + i * 18} fontSize="7" fill="#6b7280">{emp.time}</text>
              <text x="230" y={112 + i * 18} fontSize="9" fill={emp.color} textAnchor="middle">{emp.status}</text>
            </g>
          ))}
        </g>
      )}

      {id === 2 && (
        <g>
          <rect x="10" y="8" width="240" height="184" rx="10" fill="white" stroke="#14B8A6" strokeWidth="2" />
          <rect x="10" y="8" width="240" height="36" rx="10" fill="#14B8A6" />
          <rect x="10" y="30" width="240" height="14" fill="#14B8A6" />
          <text x="130" y="30" fontSize="10" fill="white" textAnchor="middle" fontWeight="bold">Inventory Tracker</text>
          <text x="130" y="42" fontSize="7" fill="#a7f3d0" textAnchor="middle">450 SKUs • All synced</text>
          {/* Stock bars */}
          {[
            { name: 'Widget A', pct: 85, qty: '42 units', ok: true },
            { name: 'Widget B', pct: 20, qty: '8 units', ok: false },
            { name: 'Widget C', pct: 60, qty: '30 units', ok: true },
            { name: 'Widget D', pct: 95, qty: '67 units', ok: true },
            { name: 'Widget E', pct: 10, qty: '4 units', ok: false },
          ].map((item, i) => (
            <g key={i}>
              <text x="18" y={65 + i * 26} fontSize="8" fill="#1f2937">{item.name}</text>
              <text x="200" y={65 + i * 26} fontSize="7" fill={item.ok ? '#10b981' : '#ef4444'} textAnchor="end">{item.qty}</text>
              {item.ok ? null : <text x="235" y={65 + i * 26} fontSize="8" fill="#ef4444">⚠</text>}
              <rect x="18" y={68 + i * 26} width="200" height="8" rx="4" fill="#e5e7eb" />
              <rect x="18" y={68 + i * 26} width={item.pct * 2} height="8" rx="4" fill={item.ok ? '#14B8A6' : '#ef4444'} />
            </g>
          ))}
          {/* Alert */}
          <rect x="14" y="168" width="232" height="18" rx="6" fill="#fef2f2" stroke="#ef4444" strokeWidth="1" />
          <text x="130" y="180" fontSize="7" fill="#ef4444" textAnchor="middle">⚠ Widget B &amp; E below reorder point — auto-alert sent</text>
        </g>
      )}

      {id === 3 && (
        <g>
          <rect x="10" y="8" width="240" height="184" rx="10" fill="white" stroke="#14B8A6" strokeWidth="2" />
          <rect x="10" y="8" width="240" height="36" rx="10" fill="#14B8A6" />
          <rect x="10" y="30" width="240" height="14" fill="#14B8A6" />
          <text x="130" y="30" fontSize="10" fill="white" textAnchor="middle" fontWeight="bold">Order Manager</text>
          <text x="130" y="42" fontSize="7" fill="#a7f3d0" textAnchor="middle">12 active orders • 3 pending</text>
          {[
            { id: '#045', customer: 'Sharma Traders', status: 'Delivered', color: '#10b981', bg: '#f0fdf4' },
            { id: '#046', customer: 'Ravi Enterprises', status: 'In Transit', color: '#f59e0b', bg: '#fffbeb' },
            { id: '#047', customer: 'Priya Stores', status: 'Processing', color: '#14B8A6', bg: '#f0fdfa' },
            { id: '#048', customer: 'Amit & Co.', status: 'Pending', color: '#6b7280', bg: '#f9fafb' },
            { id: '#049', customer: 'Sunita Retail', status: 'Delivered', color: '#10b981', bg: '#f0fdf4' },
          ].map((order, i) => (
            <g key={i}>
              <rect x="14" y={50 + i * 26} width="232" height="22" rx="6" fill={order.bg} stroke={order.color} strokeWidth="1" />
              <text x="22" y={65 + i * 26} fontSize="8" fill="#1f2937" fontWeight="bold">{order.id}</text>
              <text x="55" y={65 + i * 26} fontSize="8" fill="#4b5563">{order.customer}</text>
              <rect x="175" y={53 + i * 26} width="62" height="14" rx="7" fill={order.color} />
              <text x="206" y={63 + i * 26} fontSize="7" fill="white" textAnchor="middle">{order.status}</text>
            </g>
          ))}
          <rect x="14" y="184" width="232" height="4" rx="2" fill="#e5e7eb" />
        </g>
      )}

      {id === 4 && (
        <g>
          <rect x="10" y="8" width="240" height="184" rx="10" fill="white" stroke="#14B8A6" strokeWidth="2" />
          <rect x="10" y="8" width="240" height="36" rx="10" fill="#14B8A6" />
          <rect x="10" y="30" width="240" height="14" fill="#14B8A6" />
          <text x="130" y="30" fontSize="10" fill="white" textAnchor="middle" fontWeight="bold">Billing System</text>
          <text x="130" y="42" fontSize="7" fill="#a7f3d0" textAnchor="middle">Auto-generated • Error-free</text>
          {/* Invoice preview */}
          <rect x="14" y="50" width="232" height="100" rx="6" fill="#f8fafc" stroke="#e2e8f0" strokeWidth="1" />
          <text x="130" y="68" fontSize="10" fill="#1f2937" textAnchor="middle" fontWeight="bold">Invoice #INV-2024-049</text>
          <text x="130" y="78" fontSize="7" fill="#6b7280" textAnchor="middle">Sharma Traders • 15 Mar 2024</text>
          <line x1="20" y1="83" x2="240" y2="83" stroke="#e2e8f0" strokeWidth="1" />
          {[
            { item: 'Product A × 5', amount: '₹2,500' },
            { item: 'Product B × 3', amount: '₹1,800' },
            { item: 'Delivery', amount: '₹200' },
          ].map((row, i) => (
            <g key={i}>
              <text x="22" y={96 + i * 14} fontSize="8" fill="#4b5563">{row.item}</text>
              <text x="238" y={96 + i * 14} fontSize="8" fill="#1f2937" textAnchor="end">{row.amount}</text>
            </g>
          ))}
          <line x1="20" y1="138" x2="240" y2="138" stroke="#14B8A6" strokeWidth="1" />
          <text x="22" y="150" fontSize="9" fill="#1f2937" fontWeight="bold">Total</text>
          <text x="238" y="150" fontSize="10" fill="#14B8A6" textAnchor="end" fontWeight="bold">₹4,500</text>
          {/* Status badges */}
          <rect x="14" y="158" width="110" height="18" rx="9" fill="#f0fdf4" stroke="#10b981" strokeWidth="1" />
          <text x="69" y="170" fontSize="7" fill="#10b981" textAnchor="middle">✓ Sent to customer</text>
          <rect x="132" y="158" width="114" height="18" rx="9" fill="#fffbeb" stroke="#f59e0b" strokeWidth="1" />
          <text x="189" y="170" fontSize="7" fill="#f59e0b" textAnchor="middle">⏰ Due in 7 days</text>
          {/* Reminder */}
          <rect x="14" y="180" width="232" height="8" rx="4" fill="#f0fdfa" />
          <text x="130" y="187" fontSize="6" fill="#14B8A6" textAnchor="middle">Auto-reminder scheduled for Mar 22</text>
        </g>
      )}

      {id === 5 && (
        <g>
          <rect x="10" y="8" width="240" height="184" rx="10" fill="white" stroke="#14B8A6" strokeWidth="2" />
          <rect x="10" y="8" width="240" height="36" rx="10" fill="#14B8A6" />
          <rect x="10" y="30" width="240" height="14" fill="#14B8A6" />
          <text x="130" y="30" fontSize="10" fill="white" textAnchor="middle" fontWeight="bold">Smart Scheduler</text>
          <text x="130" y="42" fontSize="7" fill="#a7f3d0" textAnchor="middle">0 conflicts • All synced</text>
          {/* Week view */}
          {['Mon', 'Tue', 'Wed', 'Thu', 'Fri'].map((day, i) => (
            <g key={i}>
              <text x={22 + i * 46} y="58" fontSize="7" fill="#6b7280" textAnchor="middle">{day}</text>
              <rect x={8 + i * 46} y="62" width="40" height="120" rx="4" fill="#f8fafc" stroke="#e2e8f0" strokeWidth="1" />
            </g>
          ))}
          {/* Appointments */}
          <rect x="10" y="68" width="36" height="28" rx="3" fill="#14B8A6" />
          <text x="28" y="80" fontSize="6" fill="white" textAnchor="middle">Raj</text>
          <text x="28" y="89" fontSize="5" fill="#a7f3d0" textAnchor="middle">9–10 AM</text>

          <rect x="56" y="90" width="36" height="28" rx="3" fill="#10b981" />
          <text x="74" y="102" fontSize="6" fill="white" textAnchor="middle">Priya</text>
          <text x="74" y="111" fontSize="5" fill="#a7f3d0" textAnchor="middle">10–11 AM</text>

          <rect x="102" y="68" width="36" height="28" rx="3" fill="#14B8A6" />
          <text x="120" y="80" fontSize="6" fill="white" textAnchor="middle">Amit</text>
          <text x="120" y="89" fontSize="5" fill="#a7f3d0" textAnchor="middle">9–10 AM</text>

          <rect x="148" y="100" width="36" height="28" rx="3" fill="#f59e0b" />
          <text x="166" y="112" fontSize="6" fill="white" textAnchor="middle">Sunita</text>
          <text x="166" y="121" fontSize="5" fill="white" textAnchor="middle">11–12 PM</text>

          <rect x="194" y="68" width="36" height="28" rx="3" fill="#14B8A6" />
          <text x="212" y="80" fontSize="6" fill="white" textAnchor="middle">Ravi</text>
          <text x="212" y="89" fontSize="5" fill="#a7f3d0" textAnchor="middle">9–10 AM</text>

          {/* Conflict-free badge */}
          <rect x="14" y="188" width="232" height="0" rx="4" fill="#f0fdf4" />
          <text x="130" y="192" fontSize="7" fill="#10b981" textAnchor="middle">✓ No double-bookings • Auto-reminders sent</text>
        </g>
      )}

      {id === 6 && (
        <g>
          <rect x="10" y="8" width="240" height="184" rx="10" fill="white" stroke="#14B8A6" strokeWidth="2" />
          <rect x="10" y="8" width="240" height="36" rx="10" fill="#14B8A6" />
          <rect x="10" y="30" width="240" height="14" fill="#14B8A6" />
          <text x="130" y="30" fontSize="10" fill="white" textAnchor="middle" fontWeight="bold">AI Performance Dashboard</text>
          <text x="130" y="42" fontSize="7" fill="#a7f3d0" textAnchor="middle">Real-time insights</text>
          {/* KPI cards */}
          {[
            { label: 'Revenue', value: '₹2.4L', change: '+18%', color: '#10b981' },
            { label: 'Orders', value: '142', change: '+24%', color: '#14B8A6' },
            { label: 'Margin', value: '34%', change: '+6%', color: '#f59e0b' },
          ].map((kpi, i) => (
            <g key={i}>
              <rect x={14 + i * 78} y="50" width="70" height="42" rx="6" fill="#f8fafc" stroke="#e2e8f0" strokeWidth="1" />
              <text x={49 + i * 78} y="68" fontSize="11" fill="#1f2937" fontWeight="bold" textAnchor="middle">{kpi.value}</text>
              <text x={49 + i * 78} y="78" fontSize="6" fill="#6b7280" textAnchor="middle">{kpi.label}</text>
              <text x={49 + i * 78} y="87" fontSize="7" fill={kpi.color} textAnchor="middle">{kpi.change} ↑</text>
            </g>
          ))}
          {/* Line chart */}
          <rect x="14" y="100" width="232" height="60" rx="6" fill="#f8fafc" stroke="#e2e8f0" strokeWidth="1" />
          <text x="20" y="112" fontSize="7" fill="#6b7280">Revenue Trend</text>
          <polyline points="25,148 55,138 85,142 115,128 145,120 175,115 205,108 235,100" fill="none" stroke="#14B8A6" strokeWidth="2.5" strokeLinejoin="round" />
          <polyline points="25,148 55,138 85,142 115,128 145,120 175,115 205,108 235,100" fill="url(#chartGrad)" opacity="0.2" />
          <defs>
            <linearGradient id="chartGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#14B8A6" />
              <stop offset="100%" stopColor="white" />
            </linearGradient>
          </defs>
          {/* AI insight */}
          <rect x="14" y="168" width="232" height="18" rx="6" fill="#f0fdfa" stroke="#14B8A6" strokeWidth="1" />
          <text x="130" y="180" fontSize="7" fill="#14B8A6" textAnchor="middle">🤖 AI: Widget A drives 62% of revenue — restock now</text>
        </g>
      )}
    </svg>
  );
}

export default function ManualSystemsSection() {
  const [hoveredCard, setHoveredCard] = useState<number | null>(null);
  const [transformingCard, setTransformingCard] = useState<number | null>(null);
  const navigate = useNavigate();

  const handleMouseEnter = (id: number) => {
    setHoveredCard(id);
    setTransformingCard(id);
  };

  const handleMouseLeave = () => {
    setHoveredCard(null);
    setTransformingCard(null);
  };

  return (
    <section className="relative py-24 bg-gradient-to-br from-amber-50 via-white to-teal-50 overflow-hidden">
      {/* Subtle grid background */}
      <div className="absolute inset-0 opacity-30" style={{
        backgroundImage: 'linear-gradient(#e2e8f0 1px, transparent 1px), linear-gradient(90deg, #e2e8f0 1px, transparent 1px)',
        backgroundSize: '40px 40px',
      }} />

      <div className="relative z-10 max-w-7xl mx-auto px-6">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="inline-block px-4 py-1.5 bg-amber-100 text-amber-800 rounded-full text-sm font-semibold mb-4 border border-amber-200">
            Sound familiar?
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6">
            Stop Managing Your Business<br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-teal-600 to-teal-400">
              in Notebooks and Spreadsheets
            </span>
          </h2>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto">
            Hover over each card to see the transformation — from chaos to clarity
          </p>
        </div>

        {/* Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
          {problems.map((item) => (
            <div
              key={item.id}
              className="relative group cursor-pointer"
              onMouseEnter={() => handleMouseEnter(item.id)}
              onMouseLeave={handleMouseLeave}
            >
              <div className="relative h-[380px] rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-shadow duration-500">

                {/* ── BEFORE: Hand-drawn wireframe ── */}
                <div
                  className="absolute inset-0 transition-all duration-700 ease-in-out"
                  style={{
                    opacity: hoveredCard === item.id ? 0 : 1,
                    transform: hoveredCard === item.id
                      ? 'scale(0.88) rotateY(-12deg) translateX(-30px)'
                      : 'scale(1) rotateY(0deg) translateX(0px)',
                    transformOrigin: 'left center',
                    filter: hoveredCard === item.id ? 'blur(2px)' : 'blur(0px)',
                  }}
                >
                  {/* Paper texture background */}
                  <div className="absolute inset-0 bg-gradient-to-br from-amber-50 to-yellow-50 border-2 border-amber-200 rounded-2xl" />
                  {/* Ruled lines */}
                  <div className="absolute inset-0 rounded-2xl overflow-hidden opacity-30">
                    {Array.from({ length: 20 }, (_, i) => (
                      <div key={i} className="border-b border-amber-300" style={{ height: '24px' }} />
                    ))}
                  </div>

                  {/* Hand-drawn SVG */}
                  <div className="absolute inset-0 p-4">
                    <HandDrawnBefore id={item.id} />
                  </div>

                  {/* Before label */}
                  <div className="absolute top-3 left-3 flex items-center gap-1.5 bg-red-100 border border-red-300 rounded-full px-3 py-1">
                    <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
                    <span className="text-xs font-semibold text-red-700">Before</span>
                  </div>

                  {/* Bottom title */}
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-amber-100/90 to-transparent p-4 pt-8">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 flex items-center justify-center bg-red-100 rounded-lg">
                        <i className={`${item.beforeIcon} text-red-500 text-lg`}></i>
                      </div>
                      <div>
                        <p className="font-bold text-slate-800 text-sm leading-tight">{item.beforeTitle}</p>
                        <p className="text-xs text-slate-500">{item.beforeDescription}</p>
                      </div>
                    </div>
                  </div>

                  {/* Hover hint */}
                  <div className="absolute bottom-3 right-3 opacity-60">
                    <div className="flex items-center gap-1 text-xs text-slate-500">
                      <i className="ri-cursor-line text-sm"></i>
                      <span>hover</span>
                    </div>
                  </div>
                </div>

                {/* ── AFTER: Clean digital dashboard ── */}
                <div
                  className="absolute inset-0 transition-all duration-700 ease-in-out"
                  style={{
                    opacity: hoveredCard === item.id ? 1 : 0,
                    transform: hoveredCard === item.id
                      ? 'scale(1) rotateY(0deg) translateX(0px)'
                      : 'scale(0.88) rotateY(12deg) translateX(30px)',
                    transformOrigin: 'right center',
                    filter: hoveredCard === item.id ? 'blur(0px)' : 'blur(2px)',
                  }}
                >
                  <div className="absolute inset-0 bg-gradient-to-br from-teal-50 to-cyan-50 border-2 border-teal-400 rounded-2xl" />

                  {/* Clean dashboard SVG */}
                  <div className="absolute inset-0 p-4">
                    <CleanDashboard id={item.id} />
                  </div>

                  {/* After label */}
                  <div className="absolute top-3 left-3 flex items-center gap-1.5 bg-teal-100 border border-teal-300 rounded-full px-3 py-1">
                    <div className="w-2 h-2 bg-teal-500 rounded-full" />
                    <span className="text-xs font-semibold text-teal-700">After</span>
                  </div>

                  {/* Bottom title */}
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-teal-100/90 to-transparent p-4 pt-8">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 flex items-center justify-center bg-teal-500 rounded-lg shadow-md shadow-teal-500/30">
                        <i className={`${item.afterIcon} text-white text-lg`}></i>
                      </div>
                      <div>
                        <p className="font-bold text-slate-800 text-sm leading-tight">{item.afterTitle}</p>
                        <p className="text-xs text-teal-600 font-medium">{item.afterDescription}</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* ── TRANSFORMATION FLASH EFFECT ── */}
                <div
                  className="absolute inset-0 rounded-2xl pointer-events-none z-20 transition-all duration-300"
                  style={{
                    background: 'radial-gradient(circle at center, rgba(20,184,166,0.35) 0%, transparent 70%)',
                    opacity: transformingCard === item.id ? 1 : 0,
                    transform: transformingCard === item.id ? 'scale(1)' : 'scale(0.5)',
                  }}
                />

                {/* ── SCAN LINE WIPE EFFECT ── */}
                <div
                  className="absolute inset-0 rounded-2xl pointer-events-none z-10 overflow-hidden"
                  style={{ opacity: hoveredCard === item.id ? 1 : 0 }}
                >
                  <div
                    className="absolute top-0 bottom-0 w-1 bg-gradient-to-b from-transparent via-teal-400 to-transparent"
                    style={{
                      left: hoveredCard === item.id ? '100%' : '0%',
                      transition: 'left 0.6s ease-in-out',
                      boxShadow: '0 0 12px 4px rgba(20,184,166,0.6)',
                    }}
                  />
                </div>

              </div>
            </div>
          ))}
        </div>

        {/* CTA Buttons */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
          <button
            onClick={() => navigate('/request-quote', { state: { mode: 'ai' } })}
            className="group relative px-8 py-4 bg-gradient-to-r from-teal-600 to-teal-500 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 whitespace-nowrap cursor-pointer"
          >
            <span className="relative z-10 flex items-center gap-2">
              Tell Us What You Want to Automate
              <i className="ri-arrow-right-line text-xl group-hover:translate-x-1 transition-transform"></i>
            </span>
          </button>

          <button
            onClick={() => {
              const section = document.getElementById('examples-section');
              section?.scrollIntoView({ behavior: 'smooth' });
            }}
            className="px-8 py-4 bg-white text-teal-600 font-semibold rounded-xl border-2 border-teal-600 hover:bg-teal-50 transition-all duration-300 whitespace-nowrap cursor-pointer"
          >
            See What We Build
          </button>
        </div>
      </div>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Caveat:wght@400;700&display=swap');
      `}</style>
    </section>
  );
}